import { Action } from '../../../actions';
import {
	Progress,
	DownloadMode,
	DownloadStatus,
	DownloadGroupStatus,
	DownloadGroupName,
} from '../reducer';

enum DownloadManagerActionKeys {
	INIT_DOWNLOADS = '@@downloadManager/INIT_DOWNLOADS',
	INIT_PREVIOUS_DOWNLOAD_GROUPS = '@@downloadManager/INIT_PREVIOUS_DOWNLOAD_GROUPS',
	ADD_ITEM = '@@downloadManager/ADD_ITEM',
	UPDATE_STATUS = '@@downloadManager/UPDATE_STATUS',
	UPDATE_DOWNLOAD = '@@downloadManager/UPDATE_DOWNLOAD',
	SET_REQUEST_ID = '@@downloadManager/SET_REQUEST_ID',
	RECORD_DOWNLOAD_PROGRESS = '@@downloadManager/RECORD_DOWNLOAD_PROGRESS',
	REMOVE_DOWNLOAD = '@@downloadManager/REMOVE_DOWNLOAD',
	SET_DOWNLOAD_GROUP_STATUS = '@@downloadManager/SET_DOWNLOAD_GROUP_STATUS',
	PAUSE_DOWNLOAD_GROUP = '@@downloadManager/PAUSE_DOWNLOAD_GROUP',
	SET_DELETE_ALL_STATUS = '@@downloadManager/SET_DELETE_ALL_STATUS',
	SET_IS_MARKING_ITEM_FOR_DELETION = '@@downloadManager/SET_IS_MARKING_ITEM_FOR_DELETION',
	CLOSE_DOWNLOAD_GROUP_TOAST = '@@downloadManager/CLOSE_DOWNLOAD_GROUP_TOAST',
	SET_INIT_RECONCILE_IN_PROGRESS = '@@downloadManager/SET_INIT_RECONCILE_IN_PROGRESS',
	ADD_SELECTED_DOWNLOAD_ITEM = '@@downloadManager/ADD_SELECTED_DOWNLOAD_ITEM',
	REMOVE_PENDING_SELECTED_DOWNLOAD_ITEM = '@@downloadManager/REMOVE_PENDING_SELECTED_DOWNLOAD_ITEM',
	RESET_TOTAL_SELECTED_DOWNLOAD_ITEMS = '@@downloadManager/RESET_TOTAL_SELECTED_DOWNLOAD_ITEMS',
	RESET_PENDING_SELECTED_DOWNLOAD_ITEMS = '@@downloadManager/RESET_PENDING_SELECTED_DOWNLOAD_ITEMS',
	INIT_SELECTED_DOWNLOADS = '@@downloadManager/INIT_SELECTED_DOWNLOADS',
	ADD_DOWNLOAD_IN_PROGRESS_ITEM = '@@downloadManager/ADD_DOWNLOAD_IN_PROGRESS_ITEM',
	REMOVE_DOWNLOAD_IN_PROGRESS_ITEM = '@@downloadManager/REMOVE_DOWNLOAD_IN_PROGRESS_ITEM',
	ADD_DOWNLOAD_IN_PROGRESS_DEPENDENCY = '@@downloadManager/ADD_DOWNLOAD_IN_PROGRESS_DEPENDENCY',
	REMOVE_DOWNLOAD_IN_PROGRESS_DEPENDENCY = '@@downloadManager/REMOVE_DOWNLOAD_IN_PROGRESS_DEPENDENCY',
	RESET_DOWNLOADS = '@@downloadManager/RESET_DOWNLOADS',
}

interface LocalStorageDownloadInfos {
	id: number;
	status: DownloadStatus;
	mode: DownloadMode;
	version: number;
	dependencies: number[];
	dependents: number[];
	dependenciesResolved?: boolean;
	downloadConfirmed?: boolean;
	unread?: boolean;
}

export interface SelectedDownloadsInfo {
	driveItemId: number;
}

interface InitPreviousDownloadGroupsAction extends Action {
	type: DownloadManagerActionKeys.INIT_PREVIOUS_DOWNLOAD_GROUPS;
	downloadGroupStatus: DownloadGroupStatus;
	downloadImportantStatus: DownloadGroupStatus;
	downloadSelectedStatus: DownloadGroupStatus;
}

interface InitDownloadsAction extends Action {
	type: DownloadManagerActionKeys.INIT_DOWNLOADS;
	downloads: LocalStorageDownloadInfos[];
}

interface InitSelectedDownloadsAction extends Action {
	type: DownloadManagerActionKeys.INIT_SELECTED_DOWNLOADS;
	pendingSelectedDownloads: SelectedDownloadsInfo[];
	totalSelectedDownloadsCount: number;
}

interface AddItemAction extends Action {
	type: DownloadManagerActionKeys.ADD_ITEM;
	id: number;
	mode: DownloadMode;
	dependencies: number[];
	dependents: number[];
	version: number;
	dependenciesResolved: boolean;
}

interface AddSelectedDownloadItemAction extends Action {
	type: DownloadManagerActionKeys.ADD_SELECTED_DOWNLOAD_ITEM;
	driveItemId: number;
}

interface AddDownloadInProgressItemAction extends Action {
	type: DownloadManagerActionKeys.ADD_DOWNLOAD_IN_PROGRESS_ITEM;
	driveItemId: number;
}

interface AddDownloadInProgressDependencyAction extends Action {
	type: DownloadManagerActionKeys.ADD_DOWNLOAD_IN_PROGRESS_DEPENDENCY;
	dependencyId: number;
}

interface UpdateStatusAction extends Action {
	type: DownloadManagerActionKeys.UPDATE_STATUS;
	id: number;
	status: DownloadStatus;
}

interface UpdateDownloadAction extends Action {
	type: DownloadManagerActionKeys.UPDATE_DOWNLOAD;
	id: number;
	mode: DownloadMode;
	dependencies: number[];
	dependents: number[];
	version: number;
	dependenciesResolved?: boolean;
	downloadConfirmed?: boolean;
}

interface SetRequestIdAction extends Action {
	type: DownloadManagerActionKeys.SET_REQUEST_ID;
	id: number;
	requestId: number;
	isUpdating: boolean;
}

interface RecordDownloadProgressAction extends Action {
	type: DownloadManagerActionKeys.RECORD_DOWNLOAD_PROGRESS;
	id: number;
	progress: Progress;
}

interface SetDownloadGroupStatusAction extends Action {
	type: DownloadManagerActionKeys.SET_DOWNLOAD_GROUP_STATUS;
	group: DownloadGroupName;
	status: DownloadGroupStatus;
}

interface PauseDownloadGroupAction extends Action {
	type: DownloadManagerActionKeys.PAUSE_DOWNLOAD_GROUP;
	group: DownloadGroupName;
}

interface SetDeleteAllStatusAction extends Action {
	type: DownloadManagerActionKeys.SET_DELETE_ALL_STATUS;
	isDeletingAll: boolean;
}

interface SetIsMarkingItemForDeletionAction extends Action {
	type: DownloadManagerActionKeys.SET_IS_MARKING_ITEM_FOR_DELETION;
	isMarkingItemForDeletion: boolean;
}

interface RemoveDownloadAction extends Action {
	type: DownloadManagerActionKeys.REMOVE_DOWNLOAD;
	id: number;
}

interface RemovePendingSelectedDownloadAction extends Action {
	type: DownloadManagerActionKeys.REMOVE_PENDING_SELECTED_DOWNLOAD_ITEM;
	driveItemId: number;
}

interface RemoveDownloadInProgressItemAction extends Action {
	type: DownloadManagerActionKeys.REMOVE_DOWNLOAD_IN_PROGRESS_ITEM;
	driveItemId: number;
}

interface RemoveDownloadInProgressDependencyAction extends Action {
	type: DownloadManagerActionKeys.REMOVE_DOWNLOAD_IN_PROGRESS_DEPENDENCY;
	dependencyId: number;
}

interface ResetTotalSelectedDownloadsAction extends Action {
	type: DownloadManagerActionKeys.RESET_TOTAL_SELECTED_DOWNLOAD_ITEMS;
}

interface ResetPendingSelectedDownloadsAction extends Action {
	type: DownloadManagerActionKeys.RESET_PENDING_SELECTED_DOWNLOAD_ITEMS;
}

interface ResetDownloadsAction extends Action {
	type: DownloadManagerActionKeys.RESET_DOWNLOADS;
}

interface CloseDownloadGroupToastAction extends Action {
	type: DownloadManagerActionKeys.CLOSE_DOWNLOAD_GROUP_TOAST;
}

interface SetInitReconcileInProgressAction extends Action {
	type: DownloadManagerActionKeys.SET_INIT_RECONCILE_IN_PROGRESS;
	inProgress: boolean;
}

type DownloadManagerAction = InitPreviousDownloadGroupsAction
	| InitDownloadsAction
	| AddItemAction
	| UpdateStatusAction
	| UpdateDownloadAction
	| SetRequestIdAction
	| RecordDownloadProgressAction
	| RemoveDownloadAction
	| SetDownloadGroupStatusAction
	| PauseDownloadGroupAction
	| SetDeleteAllStatusAction
	| SetIsMarkingItemForDeletionAction
	| CloseDownloadGroupToastAction
	| AddSelectedDownloadItemAction
	| RemovePendingSelectedDownloadAction
	| InitSelectedDownloadsAction
	| ResetTotalSelectedDownloadsAction
	| ResetPendingSelectedDownloadsAction
	| AddDownloadInProgressItemAction
	| RemoveDownloadInProgressItemAction
	| AddDownloadInProgressDependencyAction
	| RemoveDownloadInProgressDependencyAction
	| ResetDownloadsAction
	| SetInitReconcileInProgressAction;


export {
	DownloadManagerActionKeys,
	DownloadManagerAction,
	LocalStorageDownloadInfos,
	SetDownloadGroupStatusAction,
	SetDeleteAllStatusAction,
	SetIsMarkingItemForDeletionAction,
	InitDownloadsAction,
	AddItemAction,
	UpdateStatusAction,
	UpdateDownloadAction,
	SetRequestIdAction,
	RecordDownloadProgressAction,
	RemoveDownloadAction,
	CloseDownloadGroupToastAction,
	SetInitReconcileInProgressAction,
	InitPreviousDownloadGroupsAction,
	AddSelectedDownloadItemAction,
	RemovePendingSelectedDownloadAction,
	InitSelectedDownloadsAction,
	ResetTotalSelectedDownloadsAction,
	ResetPendingSelectedDownloadsAction,
	AddDownloadInProgressItemAction,
	RemoveDownloadInProgressItemAction,
	AddDownloadInProgressDependencyAction,
	RemoveDownloadInProgressDependencyAction,
	ResetDownloadsAction,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DownloadManager/actions/typings.ts